/**
 * TanStack Query hooks for a single client card (KAN-639).
 *
 * The transcript modal is opened from /clients and from dashboard cards; the
 * header loads the client (company) and its links by client id here, so it
 * works the same from every place. The ['client', id] key is shared with
 * useManualChat, so the client is fetched once.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { clientsApi } from './clients';
import {
  ATTENTION_QUERY_KEY,
  DASHBOARD_FUNNEL_QUERY_KEY,
  FUNNEL_ANALYTICS_QUERY_KEY,
} from './queryKeys';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import type {
  Client,
  ClientLink,
  ClientLinkDecision,
  LeadCard,
} from '@/types/client';

export const clientQueryKey = (id: string | null) => ['client', id] as const;
export const clientLinksQueryKey = (id: string | null) =>
  ['client-links', id] as const;
export const clientLeadCardQueryKey = (id: string | null) =>
  ['client-lead-card', id] as const;

/** Client card by id (detail endpoint). */
export function useClient(clientId: string | null, enabled = true) {
  return useQuery<Client>({
    queryKey: clientQueryKey(clientId),
    queryFn: () => clientsApi.getById(clientId as string),
    enabled: enabled && !!clientId,
  });
}

/** Links of the client card (suggested and confirmed). */
export function useClientLinks(clientId: string | null, enabled = true) {
  return useQuery<ClientLink[]>({
    queryKey: clientLinksQueryKey(clientId),
    queryFn: () => clientsApi.getLinks(clientId as string),
    enabled: enabled && !!clientId,
  });
}

/** KAN-665: the lead card (steps for the manager) of the client. */
export function useClientLeadCard(clientId: string | null, enabled = true) {
  return useQuery<LeadCard>({
    queryKey: clientLeadCardQueryKey(clientId),
    queryFn: () => clientsApi.getLeadCard(clientId as string),
    enabled: enabled && !!clientId,
  });
}

/** KAN-690: send the lead card to e-mail; toast with the addresses or the error. */
export function useSendLeadCard(clientId: string | null) {
  return useMutation({
    mutationFn: () => clientsApi.sendLeadCard(clientId as string),
    onSuccess: (data) => {
      toast.success(`Карточка отправлена на ${data.sent_to.join(', ')}`);
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Не удалось отправить карточку'));
    },
  });
}

/** Confirm or reject a suggested link; refreshes the links of the card. */
export function useDecideClientLink(clientId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      linkId,
      decision,
    }: {
      linkId: string;
      decision: ClientLinkDecision;
    }) => clientsApi.decideLink(linkId, decision),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client-links'] });
      void queryClient.invalidateQueries({ queryKey: clientQueryKey(clientId) });
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Не удалось сохранить решение по связи'));
    },
  });
}

/**
 * KAN-723: mark / unmark the client as an own (test) contact (PATCH is_own).
 * Own contacts leave "Требуют внимания" and the funnel counters, so the client
 * card, the clients list, the attention block and the funnel are refreshed.
 */
export function useSetClientOwn(clientId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (isOwn: boolean) =>
      clientsApi.update(clientId as string, { is_own: isOwn }),
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Не удалось сохранить отметку'));
    },
    onSettled: () => {
      for (const queryKey of [
        clientQueryKey(clientId),
        ['clients'],
        ATTENTION_QUERY_KEY,
        DASHBOARD_FUNNEL_QUERY_KEY,
        FUNNEL_ANALYTICS_QUERY_KEY,
      ]) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
